import { useMutation } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';
import { createRequestTypes, initialLoading, SIGN_UP_ACTION } from '~/actions';
import { clearPendingReferralCode } from '~/domain/Referral';
import { apiMethodsConst } from '~/services/methods';
import { isReferralBlockedByExistingAccount } from '../errors';
import { SignUpService } from '../services/SignUpService';
import type { ISignUpRequest, ISignUpResponse } from '../types/SignUp';

const GENERIC_ERROR_MESSAGE = 'Something went wrong, please try again.';

const signUpEvents = createRequestTypes(SIGN_UP_ACTION) as {
  REQUEST: string;
  SUCCESS: string;
  FAILURE: string;
};

export const useSignUp = () => {
  const dispatch = useDispatch();

  return useMutation<ISignUpResponse, unknown, ISignUpRequest>({
    mutationFn: (payload) => SignUpService.signUp(payload),
    onSuccess: (data) => {
      dispatch({ type: apiMethodsConst.AUTH_SIGN_UP_REQUEST, payload: data });
      dispatch(initialLoading());
      // Clear the stashed referral so a later signup on the same device
      // doesn't accidentally re-send it. Both keys: the legacy id and the v2
      // pending code (GOTU-7244) — leaving the code would autofill an
      // unrelated later signup in this browser with a stale referrer.
      localStorage.removeItem('referralId');
      clearPendingReferralCode();
    },
    onError: (error, variables) => {
      // GOTU-7434: this failure has its own dialog, raised by `SignUpForm`.
      // Staying silent here keeps the generic error modal from stacking under
      // it — the modal names only the email and would bury the lost referral.
      if (isReferralBlockedByExistingAccount(error, variables)) return;

      const message = error instanceof Error ? error.message : GENERIC_ERROR_MESSAGE;
      dispatch({
        type: signUpEvents.FAILURE,
        payload: { message },
      });
    },
  });
};
